import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, User, ArrowRight, Mail, KeyRound } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { INPUT_CLS, ERROR_CLS } from '@/components/brand/BrandKit';
import { AuthShell, AuthLink, Field, PasswordField, GoogleButton, OrDivider, startGoogleSignIn } from '@/components/account/AuthShell';
import { UaePassButton, startUaePass } from '@/components/account/UaePassButton';
import { useCustomerAuth } from '@/context/CustomerAuthContext';
import { apiError, BRAND_ACCOUNT } from '@/api/account';
import { toast } from 'sonner';

const TYPES = [
  { id: 'retail', title: 'Personal account', sub: 'Retail · B2C — individuals and families', Icon: User },
  { id: 'corporate', title: 'Corporate account', sub: 'Business · B2B — companies and fleet managers', Icon: Building2 },
];

function TypeChooser({ onPick }) {
  return (
    <div data-testid="login-type-step" className="space-y-3">
      <p className="font-body text-sm text-[#666666]">First, tell us which {BRAND_ACCOUNT} account you are signing in to or creating.</p>
      {TYPES.map(({ id, title, sub, Icon }) => (
        <button key={id} type="button" data-testid={`login-type-${id}`} onClick={() => onPick(id)}
          className="group flex w-full items-center gap-4 rounded-xl border border-black/10 p-4 text-left hover:border-[#EE5A01] hover:bg-[#FFF4ED]/60 transition-colors">
          <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-lg bg-[#F5F5F3] text-[#EE5A01] group-hover:bg-white"><Icon className="w-5 h-5" /></span>
          <span className="flex-1"><span className="block font-heading font-bold text-base text-[#121212]">{title}</span><span className="block font-body text-xs text-[#888]">{sub}</span></span>
          <ArrowRight className="w-4 h-4 text-[#999] group-hover:text-[#EE5A01] group-hover:translate-x-0.5 transition-transform" />
        </button>
      ))}
    </div>
  );
}

function CreateAccountOptions({ type }) {
  const uae = async () => { try { await startUaePass({ intent: 'login', accountType: type }); } catch (err) { toast.error(apiError(err)); } };
  const cls = 'flex items-center gap-3 rounded-lg px-3 py-3 hover:bg-[#FFF4ED] transition-colors font-heading font-bold text-sm text-[#121212] w-full text-left';
  return (
    <div data-testid="login-create-options" className="mt-8 rounded-2xl border border-black/5 bg-white p-6 shadow-[0_16px_40px_rgba(0,0,0,0.06)]">
      <h2 className="font-heading font-bold text-base text-[#121212]">Create your {BRAND_ACCOUNT} account</h2>
      <p className="mt-1 font-body text-xs text-[#888]">Link your account to Gmail, authenticate with UAE PASS, or set up an email and password.</p>
      <ul className="mt-3 divide-y divide-black/5">
        <li><button type="button" data-testid="create-with-google" onClick={startGoogleSignIn} className={cls}><span className="grid h-8 w-8 place-items-center rounded-md bg-[#F5F5F3] text-[#EE5A01]"><Mail className="w-4 h-4" /></span>Link account to Gmail<ArrowRight className="ml-auto w-4 h-4 text-[#999]" /></button></li>
        <li><button type="button" data-testid="create-with-uaepass" onClick={uae} className={cls}><span className="grid h-8 w-8 place-items-center rounded-md bg-[#121212] text-white font-heading text-[9px]">UAE</span>UAE PASS authentication<ArrowRight className="ml-auto w-4 h-4 text-[#999]" /></button></li>
        <li><Link to={`/register?type=${type}`} data-testid="create-with-email" className={cls}><span className="grid h-8 w-8 place-items-center rounded-md bg-[#F5F5F3] text-[#EE5A01]"><KeyRound className="w-4 h-4" /></span>Email and password set-up<ArrowRight className="ml-auto w-4 h-4 text-[#999]" /></Link></li>
      </ul>
    </div>
  );
}

function SignInMethods({ type, onChangeType, error, notice }) {
  const { login } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [err, setErr] = useState(error);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const label = TYPES.find((t) => t.id === type);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      await login(form);
      navigate(location.state?.from || '/account', { replace: true });
    } catch (ex) {
      setErr(apiError(ex, 'Sign in failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-methods-step">
      <div className="mb-5 flex items-center justify-between gap-3 rounded-lg bg-[#F5F5F3] px-3 py-2">
        <span data-testid="login-type-chip" className="inline-flex items-center gap-2 font-heading font-bold text-xs text-[#121212]"><label.Icon className="w-4 h-4 text-[#EE5A01]" /> {label.title}</span>
        <button type="button" data-testid="login-change-type" onClick={onChangeType} className="font-heading font-bold text-xs text-[#EE5A01] hover:underline">Change</button>
      </div>
      <UaePassButton testid="login-uaepass-btn" intent="login" accountType={type} />
      <div className="mt-3"><GoogleButton testid="login-google-btn" /></div>
      <OrDivider />
      <form onSubmit={submit} className="space-y-5" noValidate>
        {notice && <p data-testid="login-notice" className="font-body text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">{notice}</p>}
        {err && <p data-testid="login-error" role="alert" className={ERROR_CLS}>{err}</p>}
        <Field label="Email" htmlFor="login-email">
          <Input id="login-email" data-testid="login-email" type="email" value={form.email} onChange={set('email')} placeholder={type === 'corporate' ? 'you@company.com' : 'you@example.com'} autoComplete="email" className={INPUT_CLS} required />
        </Field>
        <Field label="Password" htmlFor="login-password">
          <PasswordField id="login-password" testid="login-password" value={form.password} onChange={set('password')} />
        </Field>
        <div className="flex items-center justify-end -mt-2"><AuthLink to="/forgot-password" testid="login-forgot-link">Forgot password?</AuthLink></div>
        <button type="submit" data-testid="login-submit" disabled={loading || !form.email || !form.password} className="btn-primary w-full disabled:opacity-50">{loading ? 'Signing in…' : 'Sign in'}</button>
      </form>
    </div>
  );
}

export default function LoginPage() {
  const { customer, ready } = useCustomerAuth();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const type = ['retail', 'corporate'].includes(params.get('type')) ? params.get('type') : '';
  const error = location.state?.error || params.get('error') || '';
  const notice = location.state?.notice || '';

  useEffect(() => { document.title = `Sign in | ${BRAND_ACCOUNT}`; }, []);

  if (ready && customer) return <Navigate to="/account" replace />;

  const pick = (t) => setParams((p) => { const n = new URLSearchParams(p); n.set('type', t); n.delete('error'); return n; }, { replace: true });
  const reset = () => setParams((p) => { const n = new URLSearchParams(p); n.delete('type'); return n; }, { replace: true });

  return (
    <AuthShell testid="login-page" eyebrow={BRAND_ACCOUNT} title={type ? 'Sign in to your account' : 'Welcome back'} lead={type ? 'Use UAE PASS, Google or your email and password.' : 'Personal and corporate customers, one secure login.'}
      footer={type ? <CreateAccountOptions type={type} /> : <>New here? <AuthLink to="/register" testid="login-to-register">Create an account</AuthLink></>}>
      {!type && error && <p data-testid="login-error" role="alert" className={`${ERROR_CLS} mb-4`}>{error}</p>}
      {type ? <SignInMethods key={type} type={type} onChangeType={reset} error={error} notice={notice} /> : <TypeChooser onPick={pick} />}
    </AuthShell>
  );
}
